import { useEffect, useRef } from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { formatDateForNav } from "@/lib/dateUtils";
import { cn } from "@/lib/utils";

interface ActiveDates {
  later_dates: string[];
  current: string;
  earlier_dates: string[];
}

interface TopNavByDatesProps {
  activeDates: ActiveDates | undefined;
  currentDate: string | undefined;
  setCurrentDate: (date: string) => void;
}

export function TopNavByDates({
  activeDates,
  currentDate,
  setCurrentDate,
}: TopNavByDatesProps) {
  const selectedRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [currentDate, activeDates]);

  if (!activeDates) {
    return null;
  }

  const mergedDates = [
    ...activeDates.later_dates,
    activeDates.current,
    ...activeDates.earlier_dates,
  ];

  const renderDateButton = (date: string) => {
    const { dayName, dayNumber } = formatDateForNav(date);
    const isActive = date === currentDate;

    return (
      <button
        key={date}
        ref={isActive ? selectedRef : null}
        onClick={() => {
          window.history.pushState({}, "", `/workouts/date/${date}`);
          setCurrentDate(date);
        }}
        className={cn(
          "flex flex-col items-center justify-center w-14 h-14 rounded-2xl text-center cursor-pointer transition-all duration-300 ease-in-out",
          isActive
            ? "bg-primary text-primary-foreground shadow-md"
            : "bg-bg-muted text-tx-secondary hover:bg-accent-bg-hover"
        )}
      >
        <span className="text-[11px] font-medium uppercase tracking-wide leading-tight">
          {dayName}
        </span>
        <span className="text-lg font-bold mt-0.5 leading-tight">
          {dayNumber}
        </span>
      </button>
    );
  };

  return (
    <ScrollArea className="w-full whitespace-nowrap rounded-md border-none">
      <div className="flex w-max space-x-2 px-6 py-3">
        {mergedDates.map(renderDateButton)}
      </div>
      <ScrollBar orientation="horizontal" className="mt-2" />
    </ScrollArea>
  );
}
